import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useAuth } from "@/contexts/AuthContext";
import { db } from "@/lib/firebase";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useState, type ReactNode } from "react";
import { Loader2, Save, FolderOpen, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface FieldConfig {
  name: string;
  label: string;
  type: "text" | "email" | "number" | "textarea" | "select" | "checkbox" | "tel";
  placeholder?: string;
  description?: string;
  options?: { value: string; label: string }[];
}

interface DynamicFormProps {
  schema: z.ZodObject<any>;
  fields: FieldConfig[];
  title: string;
  description: string;
  collectionName: string;
  onSuccess?: () => void;
  /** Optional localStorage key prefix for Save/Load draft. Omit to hide. */
  draftKey?: string;
  /** Optional content rendered at the top of the form (e.g. deal selector). */
  extraHeader?: ReactNode;
  /** When true, show a "Back to dashboard" button in the sticky bar. */
  showBackButton?: boolean;
  /** Path to navigate to when Back is clicked. */
  backPath?: string;
}

export default function DynamicForm({
  schema,
  fields,
  title,
  description,
  collectionName,
  onSuccess,
  draftKey,
  extraHeader,
  showBackButton,
  backPath = "/dashboard",
}: DynamicFormProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSaveDraft = () => {
    if (!draftKey) return;
    try {
      localStorage.setItem(draftKey, JSON.stringify({ values: form.getValues(), savedAt: new Date().toISOString() }));
      toast({ title: "Draft saved", description: "Saved locally — you can come back later" });
    } catch {
      toast({ title: "Save failed", variant: "destructive" });
    }
  };

  const handleLoadDraft = () => {
    if (!draftKey) return;
    try {
      const raw = localStorage.getItem(draftKey);
      if (!raw) { toast({ title: "No draft found", variant: "destructive" }); return; }
      const p = JSON.parse(raw);
      if (p.values) form.reset(p.values);
      toast({ title: "Draft loaded" });
    } catch {
      toast({ title: "Load failed", variant: "destructive" });
    }
  };

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: fields.reduce((acc, field) => {
      acc[field.name] = field.type === "checkbox" ? false : "";
      return acc;
    }, {} as Record<string, any>),
  });

  const onSubmit = async (data: any) => {
    if (!user) {
      toast({
        title: "Authentication Required",
        description: "Please log in to submit this form",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await addDoc(collection(db, collectionName), {
        ...data,
        userId: user.id,
        userEmail: user.email,
        createdAt: serverTimestamp(),
        status: "submitted",
      });

      toast({
        title: "Success",
        description: "Form submitted successfully",
      });

      form.reset();
      onSuccess?.();
    } catch (error) {
      console.error("Error submitting form:", error);
      toast({
        title: "Error",
        description: "Failed to submit form. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderField = (field: FieldConfig) => {
    return (
      <FormField
        key={field.name}
        control={form.control}
        name={field.name}
        render={({ field: formField }) => (
          <FormItem>
            <FormLabel>{field.label}</FormLabel>
            <FormControl>
              {field.type === "select" && field.options ? (
                <Select onValueChange={formField.onChange} defaultValue={formField.value}>
                  <SelectTrigger>
                    <SelectValue placeholder={field.placeholder || "Select an option"} />
                  </SelectTrigger>
                  <SelectContent>
                    {field.options.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : field.type === "textarea" ? (
                <Textarea
                  {...formField}
                  placeholder={field.placeholder}
                  rows={4}
                />
              ) : field.type === "checkbox" ? (
                <div className="flex items-center space-x-2">
                  <Checkbox
                    checked={formField.value}
                    onCheckedChange={formField.onChange}
                  />
                  <span className="text-sm">{field.description}</span>
                </div>
              ) : (
                <Input
                  {...formField}
                  type={field.type}
                  placeholder={field.placeholder}
                />
              )}
            </FormControl>
            {field.description && field.type !== "checkbox" && (
              <FormDescription>{field.description}</FormDescription>
            )}
            <FormMessage />
          </FormItem>
        )}
      />
    );
  };

  return (
    <div className="max-w-3xl mx-auto space-y-4">
      {(showBackButton || draftKey) && (
        <div className="sticky top-0 z-30 bg-background/95 backdrop-blur border-b py-3 flex flex-wrap items-center gap-2">
          {showBackButton && (
            <Button variant="outline" size="sm" onClick={() => navigate(backPath)}>
              <ArrowLeft className="w-4 h-4 mr-1" /> Back to dashboard
            </Button>
          )}
          {draftKey && (
            <div className="ml-auto flex gap-2">
              <Button variant="outline" size="sm" onClick={handleLoadDraft}>
                <FolderOpen className="w-4 h-4 mr-1" /> Load draft
              </Button>
              <Button variant="outline" size="sm" onClick={handleSaveDraft} className="border-amber-500/60 text-amber-700 hover:bg-amber-500/10">
                <Save className="w-4 h-4 mr-1" /> Save draft
              </Button>
            </div>
          )}
        </div>
      )}
      {extraHeader}
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              {fields.map((field) => renderField(field))}
              <Button type="submit" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  "Submit"
                )}
              </Button>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
